import * as React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { FlyoutSaveCancel } from '../../../components/flyout-components/FlyoutSaveCancel';
import { CurrencyInput } from '../../../components/form-elements/CurrencyInput';
import { DateTimeInput } from '../../../components/form-elements/DateTimeInput';
import { TextArea } from '../../../components/form-elements/TextArea';
import { TextInput } from '../../../components/form-elements/TextInput';
import { ToggleInput } from '../../../components/form-elements/ToggleSwitch';
import { LoadingInlinePanel } from '../../../components/loading/LoadingInline';
import { PageHeading } from '../../../components/page-components/PageHeading';
import { PagedList } from '../../../components/paged-list/PagedList';
import { ShipmentTruckTotalsBar } from './ShipmentTruckTotalsBar';
import { LocalTimeRead } from '../../../models/utilities/local-time-read';
import { PagedApiResult } from '../../../models/utilities/paged-request';
import { StatusCode } from '../../../utils/api';
import { GlobalConstants } from '../../../utils/app-constants';
import { TransportationUtils } from '../../helpers';
import { ShipmentActionType } from '../../models/enums/shipment-action-type';
import { ShipmentMethod } from '../../models/enums/shipment-method';
import { ShipmentType } from '../../models/enums/shipment-type';
import { DELIVERABLE_SHIPMENT_STATUS, OrderSearchFilters } from '../../models/filters/order-search-filters';
import { OrderSummary } from '../../models/shared/order-summary';
import { RunningTotal } from '../../models/shared/running-total';
import { DeliveryShipmentAction } from '../../models/shipments/shipment-action';
import { DeliveryShipmentActionCreate } from '../../models/shipments/shipment-action-create';
import { TransportationSvc } from '../../service';
import ShipmentActionAddOrderRow from './ShipmentActionAddOrderRow';
import { ShipmentActionCustomerDeliveryInfo } from './ShipmentActionCustomerDeliveryInfo';
import { getCurrentServiceCenter } from '../../../selectors/userdata-selectors';
import { ShipmentStatus } from '../../models/enums/shipment-status';

interface IProps {
    stop : DeliveryShipmentAction;
    onSave: any;
    onCancel: any;
    visible: boolean;
    truckTotals: RunningTotal|undefined;
    orderList: OrderSummary[]|undefined;
    truckId: number;
    truckVersion: string;
    changeAddress: any|undefined;
    entityType: ShipmentType;
    shipmentStatus?: ShipmentStatus; 
    serviceCenterId: string;

    // Quick Create Related
    isQuickCreate       : boolean;
    updateStops?        : any;
    updateTruck?        : any;
    updateTotals?       : any;
    planningFilters?    : OrderSearchFilters;
}

let IGNORE_UNMOUNT_SELECTABLE = false;

export const ShipmentEditCustomerDelivery = (props: IProps) => {

    const store = useStore();
    const dispatch = useDispatch();
    const [service] = React.useState(new TransportationSvc());
    const [filterParams] = React.useState(new OrderSearchFilters(ShipmentMethod.Delivery));
    const [selectedTab, setSelectedTab] = React.useState(0);
    const [toAdd, setToAdd] = React.useState<OrderSummary[]>([]);
    const [toRemove, setToRemove] = React.useState<OrderSummary[]>([]);
    const [isFetching, setIsFetching] = React.useState(true);
    const [fullTruckTotal, setFullTruckTotal] = React.useState<RunningTotal>(new RunningTotal());
    const [editStop, setEditStop] = React.useState<DeliveryShipmentAction>(new DeliveryShipmentAction());

    const [ordersOnTruck, setOrdersOnTruck] = React.useState<PagedApiResult<OrderSummary>>(() => {
        const initial = new PagedApiResult<OrderSummary>();
        initial.pagination = filterParams.paginationInfo;
        return initial;
    });

    const [availableOrders, setAvailableOrders] = React.useState<PagedApiResult<OrderSummary>>(() => {
        const initial = new PagedApiResult<OrderSummary>();
        initial.pagination = filterParams.paginationInfo;
        return initial;
    });

    const TAB_ORDERS_ON_TRUCK = 0;
    const TAB_ORDERS_AVAILABLE = 1;

    /**
     * Hydrate the required properties for editing
     */
    React.useEffect(() => {
        if (props.visible && props.stop) {

            const tmpStop = { ...props.stop };
            // tmpStop.isFixedFreight = tmpStop.fixedFreightAmount ? true : false;
            setEditStop(tmpStop);

            const initial = { ...ordersOnTruck };
            initial.results = [...tmpStop.orders];
            setOrdersOnTruck(initial);

            if (canEditOrders()) {
                if (props.orderList && props.orderList.length > 0) {
                    const orders = new PagedApiResult<OrderSummary>();
                    orders.results = props.orderList;
                    setAvailableOrders(orders);
                    setIsFetching(false);
                }
                else {
                    getAvailableOrders(tmpStop.shipToAddressId);
                }
            }

            const totals = TransportationUtils.calculateTruckTotals(props.truckTotals, tmpStop.orders, []);
            if (totals) {
                setFullTruckTotal(totals);
            }

            setSelectedTab(0);
        }
    }, [props.visible]);

    function canEditOrders() {
        return props.shipmentStatus === undefined
            || props.shipmentStatus < ShipmentStatus.InProduction
            || !isProductionServiceCenter()
    }

    function isProductionServiceCenter(): boolean {
        return ["IN", "MAIN"].indexOf(props.serviceCenterId) > -1
    }

    /**
     * Handler for when the user is done drag selecting items
     * @param evt The draggable event with selected items
     */
    const onDragComplete = (evt: any) => {

        // The select library we use calls dragComplete
        // whenever a component is unmounted.  However,
        // that causes issues.  Hacky, but it's what's 
        // required without changing implemenation.
        if (IGNORE_UNMOUNT_SELECTABLE) {
            return;
        }

        let o;
        if (selectedTab === TAB_ORDERS_ON_TRUCK) {
            o = [...ordersOnTruck.results];
        }
        else {
            o = [...availableOrders.results];
        }

        for (let i = 0; i < o.length; i++) {

            let foundEvtMatch = false;
            const orderItem = o[i];

            for (let j = 0; j < evt.length; j++) {
                const evtItem = evt[j];
                if (evtItem.props.orderItem.id === orderItem.id) {
                    foundEvtMatch = true;
                    break;
                }
            }

            o[i].isChecked = (foundEvtMatch) ? true : false;
        }

        // Update the running totals
        const selecteds = o.filter((itm: OrderSummary) => {
            return itm.isChecked;
        });

        if (selectedTab === TAB_ORDERS_ON_TRUCK) {
            const totals = TransportationUtils.subtractTruckTotals(props.truckTotals, ordersOnTruck.results, selecteds);
            if (totals) {
                setFullTruckTotal(totals);
            }

            if (props.isQuickCreate) {
                props.updateTotals(totals, editStop.tmpClientId);
            }

            setToRemove(selecteds);
        }
        else {
            const totals = TransportationUtils.calculateTruckTotals(props.truckTotals, ordersOnTruck.results, selecteds);
            if (totals) {
                setFullTruckTotal(totals);
            }

            if (props.isQuickCreate) {
                props.updateTotals(totals, editStop.tmpClientId);
            }

            setToAdd(selecteds);
        }

    };

    /**
     * Clear selections and flip tabs
     * @param e The React event
     * @param val A 1 or 0 indicating which tab is active
     */
    const onTabChange = (e: any, val: any) => {
        clearSelectedOrders();
        setSelectedTab(val);
    };

    /**
     * Handle the freight amount changing
     * @param e The react event
     * @param newValue The new currency value
     */
    const handleFreightChange = (e: React.FormEvent<HTMLInputElement>, newValue: number) => {
        const t = { ...editStop };
        t.fixedFreightAmount = newValue;
        setEditStop(t);

        if (props.isQuickCreate) {
            props.updateTruck(t, props.stop.tmpClientId);
        }
    };

    /**
     * Handler for changing the shipment date.
     * @param newDate The new shipment date as chosen 
     */
    const onCutoffDateChange = (newDate: Date) => {
        const t = { ...editStop };

        if (!t.actionTime) {
            t.actionTime = new LocalTimeRead();
        }

        t.actionTime.local = newDate;
        setEditStop(t);

        if (props.isQuickCreate) {
            props.updateTruck(t, props.stop.tmpClientId);
        }
    };

    /**
     * Handler for changing the add on count
     * @param e The text input event 
     */
    const onAddOnCountChange = (e: any) => {

        try {
            const t = { ...editStop };
            t.addOnCount = Number.parseInt(e.target.value, 10);
            setEditStop(t);

            if (props.isQuickCreate) {
                props.updateTruck(t, props.stop.tmpClientId);
            }
        }
        catch (err) {
            console.log(err);
        }

    };

    /**
     * Handler for changing the add on weight
     * @param e The text input event 
     */
    const onAddOnWeightChange = (e: any) => {
        try {
            const t = { ...editStop };
            t.addOnWeight = Number.parseFloat(e.target.value);
            setEditStop(t);

            if (props.isQuickCreate) {
                props.updateTruck(t, props.stop.tmpClientId);
            }
        }
        catch (err) {
            console.log(err);
        }
    };

    /**
     * Handle the freight freight toggle changing
     */
    const toggleFixedFreightChange = () => {
        const t = { ...editStop };
        t.isFixedFreight = !t.isFixedFreight;
        t.fixedFreightAmount = 0;

        setEditStop(t);

        if (props.isQuickCreate) {
            props.updateTruck(t, props.stop.tmpClientId);
        }
    };

    /**
     * Handler for changing the shipment note.
     * @param e The text input event 
     */
    const onNoteChange = (e: any) => {
        const t = { ...editStop };
        t.note = e.target.value;
        setEditStop(t);

        if (props.isQuickCreate) {
            props.updateTruck(t, props.stop.tmpClientId);
        }
    };

    /**
     * Called after selecting available orders.  Moves the toAdd list
     * to the editing stop and removes the toAdd list from the available
     * order list
     */
    const moveOrders = () => {

        IGNORE_UNMOUNT_SELECTABLE = true;

        if (toAdd || toRemove) {

            let tmpChangeList;
            let tmpListA;
            let tmpListB;

            if (selectedTab === TAB_ORDERS_ON_TRUCK) {
                tmpChangeList = toRemove;
                tmpListA = { ...availableOrders };
                tmpListA.results = tmpListA.results.concat(toRemove);
                tmpListB = { ...ordersOnTruck };
            }
            else {
                tmpChangeList = toAdd;
                tmpListA = { ...ordersOnTruck };
                tmpListA.results = tmpListA.results.concat(toAdd);
                tmpListB = { ...availableOrders };
            }

            for (let j = 0; j < tmpChangeList.length; j++) {
                for (let i = tmpListB.results.length - 1; i >= 0; i--) {
                    if (tmpListB.results[i].id === tmpChangeList[j].id) {
                        tmpListB.results.splice(i, 1);
                        break;
                    }
                }
            }

            if (selectedTab === TAB_ORDERS_ON_TRUCK) {
                const totals = TransportationUtils.calculateTruckTotals(props.truckTotals, tmpListB.results, []);
                if (totals) {
                    setFullTruckTotal(totals);
                }

                setAvailableOrders(tmpListA);
                setOrdersOnTruck(tmpListB);

                if (props.isQuickCreate) {
                    props.updateStops(tmpListB, props.stop.tmpClientId);
                }
            }
            else {
                const totals = TransportationUtils.calculateTruckTotals(props.truckTotals, tmpListA.results, []);
                if (totals) {
                    setFullTruckTotal(totals);
                }

                setOrdersOnTruck(tmpListA);
                setAvailableOrders(tmpListB);

                if (props.isQuickCreate) {
                    props.updateStops(tmpListA, props.stop.tmpClientId);
                }
            }

            setToAdd([]);
            setToRemove([]);

            setTimeout(() => {
                IGNORE_UNMOUNT_SELECTABLE = false;
            }, 200);

        }
    };

    /**
     * Clear all the selected orderes for both lists
     */
    const clearSelectedOrders = () => {

        IGNORE_UNMOUNT_SELECTABLE = true;

        const tmpAvail = { ...availableOrders };
        tmpAvail.results.forEach((itm: OrderSummary) => {
            itm.isChecked = false;
        });

        setToAdd([]);
        setAvailableOrders(tmpAvail);

        const tmpOn = { ...ordersOnTruck };
        tmpOn.results.forEach((itm: OrderSummary) => {
            itm.isChecked = false;
        });

        setToRemove([]);
        setOrdersOnTruck(tmpOn);

        // When changing tabs, we want to make sure to only roll up the 
        // orders from the "on truck" list 
        const totals = TransportationUtils.calculateTruckTotals(props.truckTotals, tmpOn.results, []);
        if (totals) {
            setFullTruckTotal(totals);

            if (props.isQuickCreate) {
                props.updateTotals(totals, props.stop.tmpClientId);
            }
        }

        setTimeout(() => {
            IGNORE_UNMOUNT_SELECTABLE = false;
        }, 200);
    };

    /**
     * Fetch the open orders for this address
     * @param id The ship-to address ID to fetch orders for
     */
    async function getAvailableOrders(id: number) {
        setIsFetching(true);

        const filters = props.planningFilters ? props.planningFilters : new OrderSearchFilters(ShipmentMethod.Delivery, id, undefined, undefined, undefined, undefined, undefined, undefined, DELIVERABLE_SHIPMENT_STATUS);
        const response = await service.getOrdersForShipTo(filters);
        if (response.status === StatusCode.Success) {

            if (props.isQuickCreate) {

                const truckOrderIds = props.stop.orders.map((oItms: OrderSummary) => {
                    return oItms.id;
                });

                const availableOrderIds = response.data.results.filter((itm: OrderSummary) => {
                    return !truckOrderIds.includes(itm.id);
                });

                // If we have order IDs on this stop, it's assumed this is the
                // user returning to the tab during quick create.  If so, we
                // assume that the orders on the stop should remain selected
                // and put any other available in the available tab.
                //
                // Or we've already visited this tab so we don't want to mess 
                // with the orders on the truck
                if (truckOrderIds.length > 0 || props.stop.wasSeen) {
                    response.data.results = availableOrderIds;
                    setAvailableOrders(response.data);
                }
                // If we have no orders on the stop, we'll dump everything to the truck
                else {
                    setOrdersOnTruck(response.data);
                    props.updateStops(response.data, props.stop.tmpClientId);
                }
            }
            else {
                setAvailableOrders(response.data);
            }
        }

        setIsFetching(false);
    }

    /**
     * Get the correct title to show for the orders on the truck tab
     */
    const getOrdersTitle = () => {
        if (editStop) {
            return `Orders (${ordersOnTruck.results.length})`;
        }
        else {
            return 'Orders';
        }
    };

    /**
     * Get the correct title to show for the orders available tab
     */
    const getAvailableOrdersTitle = () => {

        if (isFetching) {
            return <LoadingInlinePanel message="Fetching Orders" />;
        }
        if (availableOrders && availableOrders.results && availableOrders.results.length > 0) {
            return `Available to Add (${availableOrders.results.length})`;
        }
        else {
            return 'Available to Add (0)';
        }
    };

    const onSave = () => {

        const tmpStop = { ...editStop };

        // We're editing the stop
        if (tmpStop.id > 0) {
            tmpStop.orders = ordersOnTruck.results;
            props.onSave(tmpStop);
        }
        // We're making a new one
        else {

            const newStop = new DeliveryShipmentActionCreate();
            newStop.type = ShipmentActionType.Delivery;
            newStop.dealerId = tmpStop.dealerId;
            newStop.stopTimeLocal = tmpStop.actionTime ? tmpStop.actionTime.local : null;
            newStop.fixedFreightAmount = tmpStop.isFixedFreight ? tmpStop.fixedFreightAmount : undefined;
            newStop.shipToAddressId = tmpStop.shipToAddressId;
            newStop.rowVersion = props.truckVersion;

            if (ordersOnTruck && ordersOnTruck.results.length > 0) {
                newStop.orders = ordersOnTruck.results.map((itm: OrderSummary) => {
                    return itm.id;
                });
            }

            props.onSave(newStop);
        }
    };

    return <div className="route-edit-customer-delivery">
        {
            editStop.id > 0
                ? <div className="row">
                    <div className="col-md-12">
                        <h1>Edit Customer Delivery</h1>
                    </div>
                </div>
                : null
        }

        {/* Read Only Information */}
        {
            !props.isQuickCreate
                ? <div className="row divider-row">
                    <div className="col-md-12">
                        <ShipmentActionCustomerDeliveryInfo
                            stop={props.stop}
                            changeAddress={props.changeAddress}
                            includeFreight={false}
                            entityType={props.entityType}
                        />
                    </div>
                </div>
                : null
        }

        <div className="row">
            <div className="col-md-6 customer-delivery-freight-toggle">
                <ToggleInput
                    id="driver-status"
                    label="Is this fixed freight?"
                    name="status"
                    onClick={toggleFixedFreightChange}
                    checked={editStop.isFixedFreight}
                />
                <CurrencyInput
                    label=""
                    id="subtotal"
                    className="side-by-side"
                    currencyType={'USD'}
                    showSymbol={true}
                    disable={!editStop.isFixedFreight}
                    value={editStop.fixedFreightAmount}
                    ref={(instance) => { }}
                    onChange={handleFreightChange}
                />
            </div>
            {
                editStop.id > 0
                    ? <div className="col-md-6">
                        <TextArea
                            id="stopNote"
                            value={editStop.note}
                            defaultLines={2}
                            label="Note"
                            onChange={onNoteChange}
                        />
                    </div>
                    : <div className="col-md-6">
                        <DateTimeInput
                            id="pickupTime"
                            format={GlobalConstants.ABBR_LONG_DATE}
                            value={editStop.actionTime ? editStop.actionTime.local : undefined}
                            showTimezone={true}
                            timezone={editStop.address.timeZoneId}
                            label="Stop Time"
                            onChange={onCutoffDateChange}
                        />
                    </div>
            }
        </div>

        {
            props.entityType === ShipmentType.Container &&
                editStop.id > 0
                ? <div className="row">
                    <div className="col-6">
                        <TextInput
                            id="container-addoncount"
                            type="number"
                            label="Add-On Count"
                            name="addOnCount"
                            value={editStop.addOnCount === undefined ? '' : editStop.addOnCount}
                            onChange={onAddOnCountChange}
                        />
                    </div>

                    <div className="col-6">
                        <TextInput
                            id="container-addonweight"
                            type="number"
                            label="Add-On Weight"
                            name="addOnWeight"
                            value={editStop.addOnWeight === undefined ? '' : editStop.addOnWeight}
                            onChange={onAddOnWeightChange}
                        />
                    </div>
                </div>
                : null
        }
        {
            props.shipmentStatus === undefined || props.shipmentStatus < ShipmentStatus.InProduction || !isProductionServiceCenter()
                ? <div className="row">
                    <div className="col-md-12">

                        <PageHeading
                            title=""
                            useTabs={true}
                            tabLabels={[getOrdersTitle(), getAvailableOrdersTitle()]}
                            onTabChange={onTabChange}
                            selectedTab={selectedTab} />

                        {
                            selectedTab === TAB_ORDERS_ON_TRUCK
                                ? <PagedList
                                    noResultsMessage="There are no orders on this delivery yet."
                                    searchCallback={() => { return; }}
                                    data={ordersOnTruck}
                                    filterProps={filterParams}
                                    onFilterUpdate={() => { return; }}
                                    skipScroll={true}
                                    selectableClass="transport-order-selectable"
                                    hidePager={true}
                                    reduxDispatch={dispatch}
                                    enabledDrag={true}
                                    useClientSelectablePositioning={true}
                                    onDragComplete={onDragComplete}
                                    rowRenderer={(order: OrderSummary, extraProps: any) =>
                                        <ShipmentActionAddOrderRow key={`stop-order-${order.id}`} orderItem={order} {...extraProps} />
                                    }
                                />
                                : null
                        }
                        {
                            selectedTab === TAB_ORDERS_AVAILABLE
                                ? <PagedList
                                    noResultsMessage="No available orders found for this address."
                                    searchCallback={() => { return; }}
                                    data={availableOrders}
                                    filterProps={filterParams}
                                    onFilterUpdate={() => { return; }}
                                    skipScroll={true}
                                    selectableClass="transport-order-selectable"
                                    hidePager={true}
                                    reduxDispatch={dispatch}
                                    useClientSelectablePositioning={true}
                                    enabledDrag={true}
                                    onDragComplete={onDragComplete}
                                    rowRenderer={(order: OrderSummary, extraProps: any) =>
                                        <ShipmentActionAddOrderRow key={`stop-order-${order.id}`} orderItem={order} {...extraProps} />
                                    }
                                />
                                : null
                        }
                    </div>
                </div>
                : null
        }

        <ShipmentTruckTotalsBar
            entityType={props.entityType}
            totals={fullTruckTotal}
            showBar={!props.isQuickCreate}
        />

        {
            toAdd.length > 0 || toRemove.length > 0
                ? <div className="stopaction-add-move-orders">
                    {
                        selectedTab === TAB_ORDERS_AVAILABLE
                            ? <button
                                disabled={toAdd.length < 1 || isFetching}
                                onClick={moveOrders}>
                                Add to Stop
                            </button>
                            : null
                    }
                    {
                        selectedTab === TAB_ORDERS_ON_TRUCK
                            ? <button
                                className="negative"
                                disabled={toRemove.length < 1 || isFetching}
                                onClick={moveOrders}>
                                Remove from Stop
                            </button>
                            : null
                    }
                </div>
                : null
        }

        {
            props.onSave
                ? <FlyoutSaveCancel
                    onSave={onSave}
                    onCancel={props.onCancel}
                />
                : null
        }

    </div>;

};
